"use client";

/**
 * Універсальна таблиця з опціональним перетягуванням колонок (@dnd-kit)
 * та можливістю приховувати колонки (зберігається в localStorage).
 *
 * Щоб увімкнути перетягування на будь-якій таблиці:
 * 1. У конфігу колонок додай кожній колонці унікальний `id` (напр. id: "name", id: "price").
 * 2. У DataTable передай проп columnOrderKey="унікальний-ключ-таблиці" (напр. "monthly-releases", "top-sellers").
 * Порядок зберігається в localStorage і відновлюється при наступному відкритті.
 *
 * Для приховування колонок — передай `hiddenColumns` (Set<string>).
 */

import { useState, useMemo, useCallback } from "react";
import Link from "next/link";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DataTableProps, ColumnDef } from "./types";
import { DataTableHeader } from "./DataTableHeader";

const COLUMN_ORDER_STORAGE_PREFIX = "data-table-column-order:";

function reorderColumns<T>(
  columns: ColumnDef<T>[],
  order: string[],
): ColumnDef<T>[] {
  const withId = columns.filter(
    (c): c is ColumnDef<T> & { id: string } => !!c.id,
  );
  const withoutId = columns.filter((c) => !c.id);
  const byId = new Map(withId.map((c) => [c.id, c]));
  const ordered: ColumnDef<T>[] = [];
  for (const id of order) {
    const col = byId.get(id);
    if (col) {
      ordered.push(col);
      byId.delete(id);
    }
  }
  byId.forEach((col) => ordered.push(col));
  return [...ordered, ...withoutId];
}

function SortableHeaderCell<T>({
  column,
  currentSortKey,
  sortOrder,
  onSortChange,
}: {
  column: ColumnDef<T>;
  currentSortKey?: string | null;
  sortOrder?: "asc" | "desc" | null;
  onSortChange?: (sortKey: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id! });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative",
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <DataTableHeader
      column={column}
      currentSortKey={currentSortKey}
      sortOrder={sortOrder}
      onSortChange={onSortChange}
      sortableCellRef={setNodeRef}
      sortableCellStyle={style}
      sortableListeners={listeners}
      sortableAttributes={attributes}
    />
  );
}

function getCellClass(noPadding?: boolean) {
  return noPadding ? "p-0" : "py-1 px-2";
}

function DragAlongCell<T>({
  column,
  item,
  overlay,
}: {
  column: ColumnDef<T>;
  item: T;
  /** Stretched-link overlay rendered first (absolute, covers the whole row). */
  overlay?: React.ReactNode;
}) {
  const { isDragging, setNodeRef, transform, transition } = useSortable({
    id: column.id!,
  });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    // STATIC while idle so a stretched-link overlay placed in this cell resolves
    // its containing block to the (position:relative) <tr> and covers the WHOLE
    // row — not just this cell. `relative` only while dragging, for the zIndex
    // lift. (transform still slides the cell during drag even when static.)
    position: isDragging ? "relative" : "static",
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <td
      ref={setNodeRef}
      style={style}
      className={`${getCellClass(column.noPadding)} ${
        column.align === "center"
          ? "text-center"
          : column.align === "right"
            ? "text-right"
            : "text-left"
      } ${column.width || ""}`}
    >
      {overlay}
      {column.cell
        ? column.cell(item)
        : column.accessorKey
          ? String(item[column.accessorKey] ?? "—")
          : "—"}
    </td>
  );
}

function StaticCell<T>({
  column,
  item,
  overlay,
}: {
  column: ColumnDef<T>;
  item: T;
  /** Stretched-link overlay rendered first (absolute, covers the whole row). */
  overlay?: React.ReactNode;
}) {
  return (
    <td
      className={`${getCellClass(column.noPadding)} ${
        column.align === "center"
          ? "text-center"
          : column.align === "right"
            ? "text-right"
            : "text-left"
      } ${column.width || ""}`}
    >
      {overlay}
      {column.cell
        ? column.cell(item)
        : column.accessorKey
          ? String(item[column.accessorKey] ?? "—")
          : "—"}
    </td>
  );
}

export function DataTable<T>({
  data,
  columns,
  onRowClick,
  getRowHref,
  getRowAriaLabel,
  getRowKey,
  currentSortKey,
  sortOrder,
  onSortChange,
  columnOrderKey,
  hiddenColumns,
  className = "",
}: DataTableProps<T>) {
  const storageKey = columnOrderKey
    ? `${COLUMN_ORDER_STORAGE_PREFIX}${columnOrderKey}`
    : null;

  const defaultOrder = useMemo(
    () =>
      columns
        .filter((c): c is ColumnDef<T> & { id: string } => !!c.id)
        .map((c) => c.id),
    [columns],
  );

  const [order, setOrder] = useState<string[]>(() => {
    if (typeof window === "undefined" || !storageKey) return defaultOrder;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return defaultOrder;
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed) || !parsed.every((x) => typeof x === "string")) {
        return defaultOrder;
      }
      const stored = parsed as string[];
      const newColumns = defaultOrder.filter((id) => !stored.includes(id));
      return newColumns.length > 0 ? [...stored, ...newColumns] : stored;
    } catch {
      return defaultOrder;
    }
  });

  const orderedColumns = useMemo(
    () => (storageKey ? reorderColumns(columns, order) : columns),
    [columns, order, storageKey],
  );

  const visibleColumns = useMemo(
    () =>
      hiddenColumns && hiddenColumns.size > 0
        ? orderedColumns.filter((c) => !c.id || !hiddenColumns.has(c.id))
        : orderedColumns,
    [orderedColumns, hiddenColumns],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = order.indexOf(String(active.id));
      const newIndex = order.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;
      const newOrder = arrayMove(order, oldIndex, newIndex);
      setOrder(newOrder);
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(newOrder));
        } catch {
          // ignore
        }
      }
    },
    [order, storageKey],
  );

  const isDndEnabled = !!storageKey;

  const sortableIds = useMemo(
    () => visibleColumns.filter((c) => !!c.id).map((c) => c.id!),
    [visibleColumns],
  );

  const headerCells = visibleColumns.map((column, idx) =>
    isDndEnabled && column.id ? (
      <SortableHeaderCell
        key={column.id}
        column={column}
        currentSortKey={currentSortKey}
        sortOrder={sortOrder}
        onSortChange={onSortChange}
      />
    ) : (
      <DataTableHeader
        key={column.id ?? column.sortKey ?? (column.accessorKey ? String(column.accessorKey) : idx)}
        column={column}
        currentSortKey={currentSortKey}
        sortOrder={sortOrder}
        onSortChange={onSortChange}
      />
    ),
  );

  // Stretched-link mode: the whole row is a real <a href> so middle/cmd/right-
  // click → open in new tab works anywhere. Works in BOTH the static and the DnD
  // path — the overlay is an `absolute inset-0` link in the first cell, and that
  // cell is position:static (idle), so it resolves to the `relative` <tr> and
  // covers the full row.
  const renderBodyCells = (item: T, rowHref?: string | null, rowAriaLabel?: string) =>
    visibleColumns.map((column, colIdx) => {
      const overlay =
        rowHref && colIdx === 0 ? (
          <Link
            href={rowHref}
            aria-label={rowAriaLabel}
            className="absolute inset-0 z-[1]"
          />
        ) : undefined;
      return isDndEnabled && column.id ? (
        <DragAlongCell
          key={column.id}
          column={column}
          item={item}
          overlay={overlay}
        />
      ) : (
        <StaticCell
          key={column.id ?? colIdx}
          column={column}
          item={item}
          overlay={overlay}
        />
      );
    });

  const table = (
    <table className="w-full text-sm">
      {/* relative z-20: lift the whole header above tbody so absolutely-
          positioned header content (e.g. column tooltips) paints over the
          rows instead of being covered by later-in-DOM tbody cells. */}
      <thead className="relative z-20 bg-[#1b2838] border-b border-[#2a475e]">
        <tr>
          {isDndEnabled ? (
            <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
              {headerCells}
            </SortableContext>
          ) : (
            headerCells
          )}
        </tr>
      </thead>
      <tbody>
        {data.map((item) => {
          const key = getRowKey(item);
          // Whole-row stretched link in BOTH paths (DnD cells go position:static
          // while idle so the overlay can span the row — see DragAlongCell).
          const rowHref = getRowHref?.(item) ?? null;
          const rowAriaLabel = rowHref ? getRowAriaLabel?.(item) : undefined;
          const clickable = !!rowHref || !!onRowClick;
          return (
            <tr
              key={key}
              onClick={rowHref ? undefined : onRowClick ? () => onRowClick(item) : undefined}
              onKeyDown={
                !rowHref && onRowClick
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onRowClick(item);
                      }
                    }
                  : undefined
              }
              tabIndex={!rowHref && onRowClick ? 0 : undefined}
              role={!rowHref && onRowClick ? "button" : undefined}
              className={`border-b border-[#2a475e] last:border-b-0 hover:bg-[#1f2e3f] transition-colors ${
                clickable ? "cursor-pointer" : ""
              } ${rowHref ? "relative" : ""}`}
            >
              {isDndEnabled ? (
                <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
                  {renderBodyCells(item, rowHref, rowAriaLabel)}
                </SortableContext>
              ) : (
                renderBodyCells(item, rowHref, rowAriaLabel)
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div className={`hidden md:block overflow-x-auto ${className}`}>
      {isDndEnabled ? (
        // Explicit id avoids @dnd-kit's global incremental DndDescribedBy-N
        // counter, which otherwise produces SSR vs client hydration mismatches
        // when multiple DataTable instances render across the app.
        <DndContext
          id={`dt-${columnOrderKey}`}
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          {table}
        </DndContext>
      ) : (
        table
      )}
    </div>
  );
}
